import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import NewsListItem from "@/components/feature/NewsListItem";
import { newsArticles, newsCategories } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 10;

export default function News() {
  usePageMeta({
    title: "色戒完整版无删减hd-影视资讯",
    description:
      "色戒完整版无删减hd影视资讯频道，整理影片修复动态、幕后花絮、影展消息与观影指南，按发布时间排序，每页 10 篇。",
    keywords: "影视资讯,电影新闻,幕后花絮,影展动态,观影指南,色戒完整版无删减hd",
  });

  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [category]);

  const filtered = useMemo(
    () =>
      category === "全部"
        ? newsArticles
        : newsArticles.filter((article) => article.category === category),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const items = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <PageShell>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
            影视资讯
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            修复动态、幕后制作、影展消息与观影指南，按发布时间整理。每页展示 10 篇文章，点击标题可阅读全文。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-8">
            <div className="mb-5 flex flex-wrap items-center gap-2">
              {newsCategories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                    category === item
                      ? "border-accent-500 bg-accent-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-600 hover:border-accent-300 hover:text-accent-700"
                  }`}
                >
                  {item}
                </button>
              ))}
              <span className="ml-auto hidden text-[11px] text-foreground-400 sm:inline">
                共 {filtered.length} 篇
              </span>
            </div>

            {items.length === 0 ? (
              <EmptyState
                icon="ri-newspaper-line"
                title="暂无资讯"
                description="当前分类下还没有发布内容，可以切换其他分类，或返回首页浏览影视资料。"
                actionLabel="查看全部资讯"
                onAction={() => setCategory("全部")}
              />
            ) : (
              <div className="space-y-4">
                {items.map((article, index) => (
                  <NewsListItem
                    key={article.id}
                    article={article}
                    index={(current - 1) * PAGE_SIZE + index}
                  />
                ))}
              </div>
            )}

            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-[88px] space-y-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-fire-line text-accent-600" />
                  热门阅读
                </h2>
                <div className="mt-3 divide-y divide-background-200">
                  {newsArticles.slice(0, 5).map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/news/${article.id}`}
                      className="group flex items-start gap-3 py-3"
                    >
                      <span className="font-label text-xs font-bold text-accent-500">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="clamp-2 block text-xs leading-relaxed text-foreground-800 transition-colors group-hover:text-accent-600">
                          {article.title}
                        </span>
                        <time className="mt-1 block font-label text-[10px] text-foreground-400">
                          {article.date}
                        </time>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                <h3 className="font-heading text-sm font-semibold text-foreground-950">想看片？</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  影视分类页支持按类型、年代与题材组合筛选，也可以直接在站内搜索影片或演员名称。
                </p>
                <Link
                  to="/category"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  前往影视分类
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}